/**
 * gldn-cart-route-checkout-guard — keep the cart-drawer / cart-page checkout
 * button working when the Route (Package Protection) app takes it over.
 * issue #288 / gldn55. Same SYMPTOM as gldn25/#213 ("checkout button dead in
 * cart drawer after personalized add"), NEW cause.
 *
 * What Route does (observed live, 2026-06-29):
 *   Route clones the theme's checkout button. It stamps the real
 *     <button id="checkout" name="checkout" form="cart-form"> with
 *     data-route-ref="N" + `display:none !important`, and inserts a visible
 *     copy <button name="button-route-N" data-route-copy="N"
 *     class="cart__checkout-button button"> as its sibling in .cart__ctas.
 *   It ALSO intercepts the checkout CLICK (so un-hiding the real button is not
 *     enough — Route eats that click too). When Route's widget is degraded
 *     (console "Route Protection is not available" + its /cart.js 429s), the
 *     click handler no-ops and, because the copy is not name="checkout", there
 *     is no native fallback -> dead checkout: no submit, no redirect, no toast.
 *   The swap is async (Route loads late), so a fast click reaches the real
 *     button and "works" while a real shopper clicking after the swap is dead
 *     — the intermittency in the report.
 *
 * Fix — bypass, don't un-hide. Route hooks `click`, not a programmatic form
 *   submit / navigation, so we drive checkout straight to `/checkout` (the same
 *   primitive gldn-gift-note.js uses). This works whether Route swapped the
 *   button, intercepted the click, or both.
 *
 * Policy — GLDN has NOT deployed Route yet (client is still testing it); the
 *   takeover on production is unintended app-embed leakage:
 *     ROUTE_DEPLOYED = false (default): on a checkout click that Route owns,
 *       bypass it IMMEDIATELY (synchronous navigation — no dead click, no
 *       perceptible delay).
 *     ROUTE_DEPLOYED = true (flip when GLDN formally adopts Route): do NOT
 *       bypass — let Route own the click and only rescue a CONFIRMED dead click
 *       via the watchdog, preserving Route's protection-upsell flow.
 *   A click watchdog backstops both modes.
 *
 * Coexistence: gldn-gift-note.js redirects to checkout itself after saving
 *   pending gift details (gift box checked). We DEFER to it whenever a gift box
 *   is checked — never bypassing — so the gift-note write is never preempted; a
 *   long safety watchdog only fires if that redirect somehow never happens.
 *
 * Safety: never mutates the cart, never edits a core Horizon file, never stops
 *   propagation (gldn-cart-atc-instrument telemetry + others keep firing), and
 *   is wrapped in defensive try/catch so a guard failure can't block checkout.
 *   Leaves accelerated / Shop Pay buttons alone.
 */
(function () {
  'use strict';

  // ----- config -----------------------------------------------------------
  var ROUTE_DEPLOYED = false; // flip to true when GLDN deploys Route for real
  var WATCHDOG_MS = 600; // dead-click rescue window for the standard path
  var GIFT_WATCHDOG_MS = 3000; // safety net while gldn-gift-note saves + redirects

  var report = window.gldnReport || function () {};

  var CTA_SEL = '.cart__ctas';
  var REAL_SEL = '#checkout, button[name="checkout"][form="cart-form"]';
  var COPY_SEL = 'button[data-route-copy], button[name^="button-route"]';
  // Standard checkout CTA only — never the accelerated / wallet buttons.
  var CLICK_SEL = '#checkout, .cart__checkout-button, [name="checkout"], ' + COPY_SEL;
  var ACCEL_SEL =
    '.additional-checkout-buttons, shopify-accelerated-checkout-cart, shop-pay-button, shopify-payment-terms';
  var GIFT_CHECKED_SEL = '.gldn-gift-cell .gldn-gift-checkbox:checked';

  /** Canonical "go to checkout" — bypasses Route's click hook (it's a nav, not
   *  a click). Same primitive gldn-gift-note.js uses (window.location/'/checkout'). */
  function forceCheckout() {
    try {
      var btn = document.getElementById('checkout') || document.querySelector('button[name="checkout"][form="cart-form"]');
      if (btn && btn.disabled) return false; // empty cart — nothing to do
      window.location.href = '/checkout';
      return true;
    } catch (e) {
      return false;
    }
  }

  function isVisible(el) {
    try {
      if (!el) return false;
      if (el.hasAttribute('hidden')) return false;
      var cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      var r = el.getBoundingClientRect();
      return !!(r.width || r.height);
    } catch (e) {
      return true;
    }
  }

  /** True when Route has taken the checkout button over inside this .cart__ctas. */
  function routeOwns(cta, target) {
    try {
      if (target && target.closest && target.closest(COPY_SEL)) return true;
      if (cta && cta.querySelector(COPY_SEL)) return true;
      var real = cta && cta.querySelector(REAL_SEL);
      if (real && !isVisible(real)) return true; // real button hidden by Route
      return false;
    } catch (e) {
      return false;
    }
  }

  /** Force checkout if nothing has navigated within `ms`. Cancels the moment the
   *  page starts to unload, so a healthy native / Route / gift-note checkout is
   *  never disturbed. */
  function armWatchdog(ms, tag) {
    var navigating = false;
    var mark = function () {
      navigating = true;
    };
    window.addEventListener('pagehide', mark, { once: true });
    window.addEventListener('beforeunload', mark, { once: true });
    setTimeout(function () {
      try {
        window.removeEventListener('pagehide', mark);
        window.removeEventListener('beforeunload', mark);
        if (navigating || document.visibilityState === 'hidden') return;
        report('event', 'route_checkout_rescued_' + (tag || 'watchdog'));
        forceCheckout();
      } catch (e) {}
    }, ms);
  }

  // ----- the guard: one document-level capture click listener (proactive) ---
  // Installed at load, before Route's async swap and before any user click.
  document.addEventListener(
    'click',
    function (e) {
      try {
        var t = e.target;
        if (!t || !t.closest) return;
        if (t.closest(ACCEL_SEL)) return; // leave wallet / accelerated buttons alone
        var hit = t.closest(CLICK_SEL);
        if (!hit) return;
        var cta = hit.closest(CTA_SEL) || document;

        // A gift box is checked → gldn-gift-note owns the redirect (it saves the
        // pending gift note, then navigates). Never bypass that; only net it.
        if (document.querySelector(GIFT_CHECKED_SEL)) {
          armWatchdog(GIFT_WATCHDOG_MS, 'giftnet');
          return;
        }

        // Route owns the button and is not the deployed checkout → bypass it
        // immediately. Synchronous navigation = no dead click, no delay.
        if (!ROUTE_DEPLOYED && routeOwns(cta, t)) {
          e.preventDefault(); // suppress the (Route-hooked) native submit, avoid double-nav
          report('event', 'route_checkout_bypassed');
          forceCheckout();
          return;
        }

        // Native checkout should proceed on its own; backstop it in case Route
        // (or anything) silently swallows it.
        armWatchdog(WATCHDOG_MS, 'watchdog');
      } catch (e2) {}
    },
    true
  );
})();
